import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactFlow, { Background, Controls, MiniMap, applyNodeChanges } from 'reactflow';
import 'reactflow/dist/style.css';
import { wfApi } from '../../api/workflow.js';

const APPROVER_TYPES = ['role', 'user', 'manager', 'department_head', 'auto'];
const TRIGGER_RESOURCES = ['leave_request', 'expense', 'purchase_request', 'budget', 'release'];

const blankStep = (n) => ({
  stepOrder: n,
  name: `Step ${n}`,
  approverType: 'manager',
  approverRef: '',
  conditions: [],
  slaHours: 72,
});

export default function WorkflowBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const isEdit = !!id;

  const [name, setName] = useState('New Approval Workflow');
  const [description, setDescription] = useState('');
  const [triggerResource, setTriggerResource] = useState('leave_request');
  const [steps, setSteps] = useState([blankStep(1)]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [error, setError] = useState(null);
  const [readonly, setReadonly] = useState(false);

  // Load existing definition (read-only — new versions require POST).
  useEffect(() => {
    if (!isEdit) return;
    wfApi.getDefinition(id).then((d) => {
      const def = d.definition;
      setName(def.name);
      setDescription(def.description || '');
      setTriggerResource(def.trigger_resource);
      setReadonly(true);
      setSteps(def.steps.map((s, _i) => ({
        stepOrder: s.step_order,
        name: s.name,
        approverType: s.approver_type,
        approverRef: s.approver_ref || '',
        conditions: typeof s.conditions === 'string' ? JSON.parse(s.conditions) : s.conditions || [],
        slaHours: s.sla_hours,
      })));
      setSelectedIdx(0);
    });
  }, [id, isEdit]);

  // ---- React Flow data ----
  const nodes = useMemo(() => steps.map((s, i) => ({
    id: `s-${s.stepOrder}`,
    position: { x: 80 + i * 220, y: 60 },
    data: {
      label: (
        <div>
          <div style={{ fontWeight: 700, fontSize: 12 }}>{i + 1}. {s.name}</div>
          <div style={{ fontSize: 11, opacity: 0.7 }}>{s.approverType}{s.approverRef ? `:${s.approverRef}` : ''}</div>
          <div style={{ fontSize: 10, opacity: 0.7 }}>SLA {s.slaHours}h{s.conditions.length ? ` · ${s.conditions.length} rule(s)` : ''}</div>
        </div>
      ),
    },
    style: {
      border: i === selectedIdx ? '2px solid #2563eb' : '1px solid #cbd5e1',
      borderRadius: 8,
      background: '#fff',
      padding: 6,
      width: 200,
    },
  })), [steps, selectedIdx]);

  const edges = useMemo(() => steps.slice(1).map((s, i) => ({
    id: `e-${i}`,
    source: `s-${steps[i].stepOrder}`,
    target: `s-${s.stepOrder}`,
    animated: true,
  })), [steps]);

  const onNodeClick = useCallback((_, node) => {
    const idx = steps.findIndex((s) => `s-${s.stepOrder}` === node.id);
    if (idx >= 0) setSelectedIdx(idx);
  }, [steps]);

  const [rfNodes, setRfNodes] = useState(nodes);
  useEffect(() => setRfNodes(nodes), [nodes]);

  function addStep() {
    if (readonly) return;
    setSteps((s) => [...s, blankStep((s[s.length - 1]?.stepOrder || 0) + 1)]);
    setSelectedIdx(steps.length);
  }
  function removeStep(idx) {
    if (readonly) return;
    setSteps((s) => s.filter((_, i) => i !== idx).map((st, i) => ({ ...st, stepOrder: i + 1 })));
    setSelectedIdx(Math.max(0, idx - 1));
  }
  function patchStep(idx, patch) {
    setSteps((s) => s.map((st, i) => (i === idx ? { ...st, ...patch } : st)));
  }
  function addCondition(idx) {
    patchStep(idx, { conditions: [...steps[idx].conditions, { if: '', skip: false }] });
  }
  function patchCondition(idx, ci, patch) {
    const next = steps[idx].conditions.map((c, j) => (j === ci ? { ...c, ...patch } : c));
    patchStep(idx, { conditions: next });
  }
  function removeCondition(idx, ci) {
    patchStep(idx, { conditions: steps[idx].conditions.filter((_, j) => j !== ci) });
  }

  async function save() {
    setError(null);
    try {
      const def = await wfApi.createDefinition({
        name,
        description: description || null,
        triggerResource,
        isActive: true,
        steps: steps.map((s) => ({
          stepOrder: s.stepOrder,
          name: s.name,
          approverType: s.approverType,
          approverRef: s.approverRef || null,
          conditions: s.conditions.filter((c) => c.if || c.skip || c.skip_to_step || c.approver),
          slaHours: Number(s.slaHours) || 72,
        })),
      });
      nav(`/workflows/definitions/${def.definition.id}`);
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    }
  }

  const sel = steps[selectedIdx];

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>{isEdit ? 'Workflow Definition' : 'New Workflow'}</h1>
          {readonly && <p className="muted small">Read-only — saving creates a new version.</p>}
        </div>
        <div className="actions">
          <Link to="/workflows/definitions" className="btn ghost">← All</Link>
          {readonly && <button className="btn ghost" onClick={() => setReadonly(false)}>Edit as new version</button>}
          {!readonly && <button className="btn primary" onClick={save}>Save</button>}
        </div>
      </header>

      {error && <div className="alert error">{error}</div>}

      <div className="card form-card grid-form">
        <label>Name<input value={name} onChange={(e) => setName(e.target.value)} disabled={readonly} /></label>
        <label>
          Trigger Resource
          <select value={triggerResource} onChange={(e) => setTriggerResource(e.target.value)} disabled={readonly}>
            {TRIGGER_RESOURCES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label className="full">Description<input value={description} onChange={(e) => setDescription(e.target.value)} disabled={readonly} /></label>
      </div>

      <div className="builder">
        <div className="builder-canvas card">
          <div style={{ height: 280 }}>
            <ReactFlow
              nodes={rfNodes}
              edges={edges}
              onNodeClick={onNodeClick}
              onNodesChange={(c) => setRfNodes((n) => applyNodeChanges(c, n))}
              nodesDraggable
              nodesConnectable={false}
              fitView
            >
              <Background gap={16} />
              <Controls />
              <MiniMap pannable />
            </ReactFlow>
          </div>
          {!readonly && <button className="btn ghost small" onClick={addStep}>+ Add Step</button>}
        </div>

        {sel && (
          <div className="builder-inspector card">
            <header className="page-header">
              <h3>Step {selectedIdx + 1}</h3>
              {!readonly && steps.length > 1 && <button className="btn ghost small danger-btn" onClick={() => removeStep(selectedIdx)}>Remove</button>}
            </header>
            <div className="grid-form">
              <label className="full">Name<input value={sel.name} onChange={(e) => patchStep(selectedIdx, { name: e.target.value })} disabled={readonly} /></label>
              <label>
                Approver Type
                <select value={sel.approverType} onChange={(e) => patchStep(selectedIdx, { approverType: e.target.value })} disabled={readonly}>
                  {APPROVER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </label>
              <label>
                Approver Ref
                <input
                  value={sel.approverRef}
                  onChange={(e) => patchStep(selectedIdx, { approverRef: e.target.value })}
                  placeholder={sel.approverType === 'role' ? 'role name (e.g. hr_manager)' : sel.approverType === 'user' ? 'user UUID' : '—'}
                  disabled={readonly || ['manager', 'department_head', 'auto'].includes(sel.approverType)}
                />
              </label>
              <label>SLA hours<input type="number" min={1} value={sel.slaHours} onChange={(e) => patchStep(selectedIdx, { slaHours: Number(e.target.value) })} disabled={readonly} /></label>
            </div>

            <h4>Conditional Routing</h4>
            <p className="muted small">Each rule is checked when the step activates. The first matching rule wins.</p>
            {sel.conditions.map((c, ci) => (
              <div key={ci} className="cond-row">
                <span className="muted small">IF</span>
                <input
                  placeholder="amount &gt; 5000  •  department == 'engineering'"
                  value={c.if || ''}
                  onChange={(e) => patchCondition(selectedIdx, ci, { if: e.target.value })}
                  disabled={readonly}
                />
                <span className="muted small">THEN</span>
                <select
                  value={c.skip ? 'skip' : c.skip_to_step ? 'skip_to' : c.approver ? 'override' : ''}
                  onChange={(e) => {
                    const v = e.target.value;
                    patchCondition(selectedIdx, ci, {
                      skip: v === 'skip',
                      skip_to_step: v === 'skip_to' ? (c.skip_to_step || 1) : undefined,
                      approver: v === 'override' ? (c.approver || 'role:hr_manager') : undefined,
                    });
                  }}
                  disabled={readonly}
                >
                  <option value="">— no action —</option>
                  <option value="skip">skip this step</option>
                  <option value="skip_to">skip to step #</option>
                  <option value="override">override approver</option>
                </select>
                {c.skip_to_step !== undefined && (
                  <input type="number" min={1} value={c.skip_to_step} onChange={(e) => patchCondition(selectedIdx, ci, { skip_to_step: Number(e.target.value) })} disabled={readonly} />
                )}
                {c.approver !== undefined && (
                  <input value={c.approver} placeholder="role:tech_lead" onChange={(e) => patchCondition(selectedIdx, ci, { approver: e.target.value })} disabled={readonly} />
                )}
                {!readonly && <button className="btn ghost small" type="button" onClick={() => removeCondition(selectedIdx, ci)}>×</button>}
              </div>
            ))}
            {!readonly && <button className="btn ghost small" type="button" onClick={() => addCondition(selectedIdx)}>+ Add Rule</button>}
          </div>
        )}
      </div>
    </section>
  );
}
